/**
 * Milestone Approval Component
 * 
 * A comprehensive component for managing the mutual approval process of job milestones
 * between clients and vendors. This component handles the complex workflow where both
 * parties must approve the milestone timeline before proceeding to purchase order creation.
 * 
 * **Approval Workflow:**
 * 1. Initial State: Both parties see pending status
 * 2. First Approval: Either party approves, status updates to "approved_by_[role]"
 * 3. Second Approval: Other party approves, status becomes "approved_by_both"
 * 4. Change Request: Either party can request changes, resetting the approval cycle
 * 5. Change Response: Other party can approve or decline the requested changes
 * 
 * **Key Features:**
 * - Real-time approval status tracking for both parties
 * - Visual change history with before/after comparisons
 * - Validation that all milestones are completed (not blank)
 * - Automatic redirect to PO creation when both parties approve
 * - Change request workflow with detailed change tracking
 * - Role-based button visibility and action permissions
 * 
 * **Approval States:**
 * - `pending_both`: Neither party has approved yet
 * - `approved_by_client`: Client approved, waiting for vendor
 * - `approved_by_vendor`: Vendor approved, waiting for client
 * - `approved_by_both`: Both parties approved (ready for PO)
 * - `change_requested_by_client`: Client requested changes
 * - `change_requested_by_vendor`: Vendor requested changes
 * - `pending_client`: Waiting specifically for client action
 * - `pending_vendor`: Waiting specifically for vendor action
 * 
 * @module MilestoneApprovalComponent
 */

import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { CheckCircle, Clock, XCircle, Users, AlertTriangle, ChevronDown, ChevronUp, ArrowRight, Loader2 } from 'lucide-react';
import { useApproveMilestones, useRespondToChangeRequest, usePendingMilestoneChanges } from '@/hooks/useMilestones';
import { useAuth } from '@/lib/auth';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import type { MilestoneApproval, Milestone } from '@/lib/types/milestone';
import { cn } from '@/lib/utils';

/**
 * Props for the MilestoneApprovalComponent
 */
interface MilestoneApprovalProps {
  /** Current approval status object (null if no approval record exists yet) */
  approval?: MilestoneApproval | null;
  /** Array of milestones to be approved */
  milestones: Milestone[];
  /** Job ID for which milestones are being approved */
  jobId: string;
  /** Callback invoked when approval status changes */
  onApprovalChange?: () => void;
  /** Current user's role (determines which actions are available) */
  userRole: 'client' | 'vendor';
}

/**
 * MilestoneApprovalComponent
 * 
 * Manages the bilateral approval process for job milestones. This component ensures
 * both parties agree on the milestone timeline before proceeding with purchase order
 * creation and job execution.
 * 
 * The component automatically:
 * - Fetches and displays change history when changes are requested
 * - Validates that all milestones are complete before allowing approval
 * - Redirects to PO creation page when both parties approve (client only)
 * - Shows appropriate action buttons based on user role and current status
 * - Displays detailed before/after comparisons for requested changes
 * 
 * @param props - Component props
 * @param props.approval - Current approval status (null if none exists)
 * @param props.milestones - Milestones to approve
 * @param props.jobId - Associated job ID
 * @param props.onApprovalChange - Callback for approval updates
 * @param props.userRole - User's role (client or vendor)
 * 
 * @example
 * ```tsx
 * function MilestonesPage({ job }) {
 *   const { approval, milestones } = useMilestones(job.id);
 *   const userRole = user.role === 'client' ? 'client' : 'vendor';
 *   
 *   return (
 *     <MilestoneApprovalComponent
 *       approval={approval}
 *       milestones={milestones}
 *       jobId={job.id}
 *       userRole={userRole}
 *       onApprovalChange={() => refetchApproval()}
 *     />
 *   );
 * }
 * ```
 */
export function MilestoneApprovalComponent({
  approval, 
  milestones, 
  jobId, 
  onApprovalChange,
  userRole 
}: MilestoneApprovalProps) {
  const { approveMilestones, loading } = useApproveMilestones();
  const { respondToChange, loading: respondLoading } = useRespondToChangeRequest();
  const { pendingChanges, refetch: refetchPendingChanges } = usePendingMilestoneChanges(jobId);
  const [showChangeDetails, setShowChangeDetails] = useState(false);
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();


  const handleApprove = async () => {
    try {
      await approveMilestones(jobId, userRole);
      onApprovalChange?.();
      
      // Check if both parties have now approved
      const { data: updatedApproval } = await supabase
        .from('milestone_approvals')
        .select('*')
        .eq('job_id', jobId)
        .single();
      
      const otherParty = userRole === 'client' ? 'vendor' : 'client';
      
      // Show success animation for both parties approved
      if (updatedApproval?.approval_status === 'approved_by_both') {
        setShowSuccessAnimation(true);
        toast({
          title: '🎉 Both Parties Approved!',
          description: userRole === 'client' 
            ? 'You can now issue the purchase order to begin work.' 
            : 'The client can now issue the purchase order.',
        });
        
        // Redirect after animation
        setTimeout(async () => {
          const { data: jobData } = await supabase
            .from('jobs')
            .select('public_id')
            .eq('id', jobId)
            .single();
          
          if (jobData) {
            navigate(`/jobs/${jobData.public_id}`);
          }
        }, 2000);
      } else {
        toast({
          title: '✓ Milestones Approved',
          description: `Your approval has been recorded. Waiting for ${otherParty} to approve.`,
        });
      }
    } catch (error) {
      console.error('Failed to approve milestones:', error);
      toast({
        title: 'Error',
        description: 'Failed to approve milestones',
        variant: 'destructive',
      });
    }
  };

  const handleApproveChange = async () => {
    try {
      await respondToChange(jobId, 'approve', userRole, () => {
        onApprovalChange?.();
        refetchPendingChanges();
      });
    } catch (error) {
      console.error('Failed to approve change:', error);
    }
  };

  const handleDeclineChange = async () => {
    try {
      await respondToChange(jobId, 'decline', userRole, () => {
        onApprovalChange?.();
        refetchPendingChanges();
      });
    } catch (error) {
      console.error('Failed to decline change:', error);
    }
  };

  const getApprovalStatus = () => {
    if (!approval) return 'pending_both';
    return approval.approval_status;
  };

  const canApprove = () => {
    const status = getApprovalStatus();
    const hasBlankMilestones = milestones.some(m => m.is_blank);
    
    // Cannot approve if there are no milestones at all
    if (milestones.length === 0) return false;
    
    // Cannot approve if there are blank milestones
    if (hasBlankMilestones) return false;
    
    // Check if user can approve based on current status
    if (userRole === 'client') {
      return status === 'pending_both' || status === 'pending_client' || status === 'approved_by_vendor';
    } else {
      return status === 'pending_both' || status === 'pending_vendor' || status === 'approved_by_client';
    }
  };

  const hasUserApproved = () => {
    const status = getApprovalStatus();
    if (userRole === 'client') {
      return status === 'approved_by_client' || status === 'approved_by_both';
    } else {
      return status === 'approved_by_vendor' || status === 'approved_by_both';
    }
  };

  const getStatusIcon = () => {
    const status = getApprovalStatus();
    
    if (status === 'approved_by_both') {
      return <CheckCircle className="h-5 w-5 text-green-500" />;
    } else if (status.includes('approved')) {
      return <Clock className="h-5 w-5 text-yellow-500" />;
    } else {
      return <Clock className="h-5 w-5 text-gray-400" />;
    }
  };

  const getStatusText = () => {
    const status = getApprovalStatus();
    
    switch (status) {
      case 'approved_by_both':
        return 'Approved by both parties';
      case 'approved_by_client':
        return 'Approved by client, waiting for vendor';
      case 'approved_by_vendor':
        return 'Approved by vendor, waiting for client';
      case 'pending_client':
        return 'Waiting for client approval';
      case 'pending_vendor':
        return 'Waiting for vendor approval';
      case 'change_requested_by_client':
        return 'Client has requested changes - awaiting vendor response';
      case 'change_requested_by_vendor':
        return 'Vendor has requested changes - awaiting client response';
      default:
        return 'Waiting for approval from both parties';
    }
  };

  const getStatusColor = () => {
    const status = getApprovalStatus();
    
    if (status === 'approved_by_both') {
      return 'bg-green-100 text-green-800 border-green-200';
    } else if (status.includes('approved')) {
      return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    } else if (status.includes('change_requested')) {
      return 'bg-orange-100 text-orange-800 border-orange-200';
    } else {
      return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const isChangeRequested = () => {
    const status = getApprovalStatus();
    return status === 'change_requested_by_client' || status === 'change_requested_by_vendor';
  };

  const canRespondToChange = () => {
    const status = getApprovalStatus();
    if (userRole === 'client') {
      return status === 'change_requested_by_vendor';
    } else {
      return status === 'change_requested_by_client';
    }
  };

  const blankMilestones = milestones.filter(m => m.is_blank);

  const formatFieldName = (fieldName: string) => {
    const fieldLabels: Record<string, string> = {
      milestone_name: 'Milestone Name',
      due_date: 'Due Date',
      deliverable: 'Deliverable',
      action_owner: 'Action Owner',
      milestone_type: 'Milestone Type',
      description: 'Description',
      milestone_order: 'Order'
    };
    return fieldLabels[fieldName] || fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const formatFieldValue = (fieldName: string, value: string) => {
    if (fieldName === 'due_date') {
      try {
        return format(new Date(value), 'PPP');
      } catch {
        return value;
      }
    }
    if (fieldName === 'action_owner') {
      return value.charAt(0).toUpperCase() + value.slice(1);
    }
    if (fieldName === 'milestone_type') {
      return value.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }
    return value;
  };

  return (
    <>
      {/* Success Animation Overlay */}
      {showSuccessAnimation && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 animate-fade-in">
          <div className="bg-white p-8 rounded-lg shadow-xl animate-scale-in">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto animate-pulse" />
            <h3 className="text-xl font-bold mt-4 text-center">Milestones Approved!</h3>
            <p className="text-muted-foreground text-center mt-2">Redirecting...</p>
          </div>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Approve Project Milestones Plan
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
        {/* Approval Status */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {getStatusIcon()}
            <div>
              <p className="font-medium text-foreground">Current Status</p>
              <p className="text-sm text-muted-foreground">{getStatusText()}</p>
            </div>
          </div>
          <Badge 
            variant="outline" 
            className={cn(
              getStatusColor(),
              canApprove() && "animate-pulse"
            )}
          >
            {getApprovalStatus().replace(/_/g, ' ')}
          </Badge>
        </div>

        {/* Approval Details */}
        {approval && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <h4 className="font-medium text-sm text-foreground">Client Approval</h4>
              {approval.client_approved_at ? (
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span className="text-sm text-muted-foreground">
                    Approved on {format(new Date(approval.client_approved_at), 'PPp')}
                  </span>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-gray-400" />
                  <span className="text-sm text-muted-foreground">Pending approval</span>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <h4 className="font-medium text-sm text-foreground">Vendor Approval</h4>
              {approval.vendor_approved_at ? (
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span className="text-sm text-muted-foreground">
                    Approved on {format(new Date(approval.vendor_approved_at), 'PPp')}
                  </span>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-gray-400" />
                  <span className="text-sm text-muted-foreground">Pending approval</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Blank Milestones Warning */}
        {blankMilestones.length > 0 && (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <div className="flex items-start gap-2">
              <XCircle className="h-5 w-5 text-orange-500 mt-0.5" />
              <div>
                <h4 className="font-medium text-orange-800">Completion Required</h4>
                <p className="text-sm text-orange-700 mt-1">
                  {blankMilestones.length} milestone(s) need to be completed before approval can proceed.
                </p>
                <ul className="list-disc list-inside text-sm text-orange-700 mt-2">
                  {blankMilestones.map(milestone => (
                    <li key={milestone.id}>{milestone.milestone_name}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Change Request Alert */}
        {isChangeRequested() && (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-5 w-5 text-orange-500 mt-0.5" />
              <div className="flex-1">
                <h4 className="font-medium text-orange-800">Change Request Pending</h4>
                <p className="text-sm text-orange-700 mt-1">
                  {getStatusText()}
                </p>
                
                {/* Toggle to show/hide change details */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowChangeDetails(!showChangeDetails)}
                  className="mt-2 p-0 h-auto text-orange-700 hover:text-orange-800"
                >
                  {showChangeDetails ? (
                    <>
                      <ChevronUp className="h-4 w-4 mr-1" />
                      Hide Changes
                    </>
                  ) : (
                    <>
                      <ChevronDown className="h-4 w-4 mr-1" />
                      View Requested Changes
                    </>
                  )}
                </Button>

                {/* Change Details */}
                {showChangeDetails && (
                  <div className="mt-3 space-y-3">
                    <div className="bg-white rounded p-3 border border-orange-200">
                      <h5 className="font-medium text-orange-800 mb-3">Requested Changes</h5>
                      <div className="space-y-4">
                        {pendingChanges.length > 0 ? (
                          pendingChanges.map((pendingChange: any) => {
                            const milestone = milestones.find(m => m.id === pendingChange.milestone_id);
                            if (!milestone) return null;

                            return (
                              <div key={pendingChange.id} className="border border-orange-100 rounded p-3 bg-orange-25">
                                <h6 className="font-medium text-orange-800 mb-2">{milestone.milestone_name}</h6>
                                <div className="space-y-2">
                                  {Object.entries(pendingChange.pending_changes)
                                    .filter(([fieldName, newValue]: [string, any]) => {
                                      // Only show fields that actually changed
                                      const oldValue = milestone[fieldName as keyof Milestone];
                                      const normalizedOld = String(oldValue ?? '');
                                      const normalizedNew = String(newValue ?? '');
                                      return normalizedOld !== normalizedNew;
                                    })
                                    .map(([fieldName, newValue]: [string, any]) => {
                                    const oldValue = milestone[fieldName as keyof Milestone];
                                    return (
                                      <div key={fieldName} className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
                                        <div className="font-medium text-orange-700">
                                          {formatFieldName(fieldName)}:
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <div className="bg-red-50 border border-red-200 rounded px-2 py-1 text-red-700">
                                            <span className="text-xs font-medium">Current:</span><br />
                                            {formatFieldValue(fieldName, String(oldValue || '')) || 'Empty'}
                                          </div>
                                          <ArrowRight className="h-3 w-3 text-orange-500 shrink-0" />
                                          <div className="bg-green-50 border border-green-200 rounded px-2 py-1 text-green-700">
                                            <span className="text-xs font-medium">Proposed:</span><br />
                                            {formatFieldValue(fieldName, String(newValue || '')) || 'Empty'}
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                  {pendingChange.change_reason && (
                                    <div className="mt-2 p-2 bg-orange-50 border border-orange-200 rounded">
                                      <span className="text-xs font-medium text-orange-700">Reason for change:</span>
                                      <div className="text-sm text-orange-600 mt-1">{pendingChange.change_reason}</div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })
                        ) : (
                          <div className="text-center text-orange-600 py-4">
                            {isChangeRequested() ? 'No pending changes found' : 'Loading change details...'}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {canRespondToChange() && (
                  <div className="flex gap-2 mt-3">
                    <Button 
                      size="lg" 
                      onClick={handleApproveChange} 
                      disabled={respondLoading}
                      className="bg-green-600 hover:bg-green-700 text-white font-semibold min-w-[160px]"
                    >
                      {respondLoading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        'Approve Changes'
                      )}
                    </Button>
                    <Button 
                      size="lg" 
                      variant="outline" 
                      onClick={handleDeclineChange} 
                      disabled={respondLoading}
                      className="min-w-[160px]"
                    >
                      {respondLoading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        'Decline Changes'
                      )}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Approval Action */}
        <div className="flex flex-col items-end gap-2">
          {hasUserApproved() && !isChangeRequested() ? (
            <Badge variant="secondary" className="bg-green-100 text-green-800 text-base py-2 px-4">
              <CheckCircle className="mr-2 h-4 w-4" />
              You have approved this timeline
            </Badge>
          ) : canApprove() && !isChangeRequested() ? (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <Button 
                      onClick={handleApprove} 
                      disabled={loading || blankMilestones.length > 0}
                      size="lg"
                      className="bg-green-600 hover:bg-green-700 text-white font-semibold min-w-[200px]"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Approving...
                        </>
                      ) : (
                        <>
                          <CheckCircle className="mr-2 h-4 w-4" />
                          Approve Milestones
                        </>
                      )}
                    </Button>
                  </span>
                </TooltipTrigger>
                {blankMilestones.length > 0 && (
                  <TooltipContent>
                    Complete all blank milestones before approving
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
          ) : !isChangeRequested() ? (
            <TooltipProvider>
              <div className="flex flex-col items-end gap-2">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <Button disabled size="lg" className="min-w-[200px]">
                        Approve as {userRole}
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    {milestones.length === 0 && 'At least one milestone must be added before approval'}
                    {milestones.length > 0 && blankMilestones.length > 0 && 'Complete all milestones before approving'}
                    {milestones.length > 0 && blankMilestones.length === 0 && 'Waiting for the other party to approve first'}
                  </TooltipContent>
                </Tooltip>
              </div>
            </TooltipProvider>
          ) : null}
        </div>
      </CardContent>
    </Card>
    </>
  );
}